<template>
  <v-card class="layer-info-card" data-testid="layer-info">
    <v-toolbar flat color="primary" class="header">
      <v-icon class="ml-4 mr-2">mdi-information</v-icon>
      <v-toolbar-title data-testid="layer-info-title">{{ title }}</v-toolbar-title>
      <v-spacer />
      <v-btn
        icon
        variant="flat"
        size="x-small"
        color="rgb(var(--v-theme-background))"
        class="mr-2"
        :aria-label="$t('common.actions.close')"
        data-testid="layer-info-close"
        @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
        <v-tooltip activator="parent" location="start">{{ $t('common.actions.close') }}</v-tooltip>
      </v-btn>
    </v-toolbar>
    <v-card-text class="content">
      <p class="description" data-testid="layer-info-description">{{ translateLangsKeyObject(info.description) }}</p>
      <ul v-if="info.categories?.length" class="categories">
        <li v-for="(category, index) in info.categories" :key="index" data-testid="layer-info-category">
          <span class="swatch" :style="{ backgroundColor: category.color }" />
          {{ translateLangsKeyObject(category.text) }}
        </li>
      </ul>
      <dl v-if="info.credit || info.sourceUrl" class="credits">
        <template v-if="info.credit">
          <dt>{{ $t('views.map.layerInfo.credits') }}</dt>
          <dd data-testid="layer-info-credit">{{ info.credit }}</dd>
        </template>
        <template v-if="info.sourceUrl">
          <dt>{{ $t('views.map.layerInfo.source') }}</dt>
          <dd>
            <a :href="info.sourceUrl" target="_blank" rel="noopener noreferrer">{{ info.sourceUrl }}</a>
          </dd>
        </template>
      </dl>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import karonteI18n from '@tsyl/karonte/lib/utils/i18n'

import type { LayerInfo } from '@/types/LayerInfo'

defineProps<{
  title: string
  info: LayerInfo
}>()

defineEmits<{
  (event: 'close'): void
}>()

const { translateLangsKeyObject } = karonteI18n(`sa`, {})
</script>

<style lang="scss" scoped>
.header {
  position: sticky;
  top: 0;
}

.content {
  padding: 1.5em 2em 2em;
}

.description {
  white-space: pre-line;
}

.categories {
  list-style: none;
  margin-top: 1em;

  li {
    display: flex;
    align-items: center;
    margin-bottom: 0.25em;
  }
}

.swatch {
  border: 1px solid white;
  width: 3em;
  height: 1.5em;
  margin-right: 0.5em;
}

.credits {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.25em 0.5em;
  margin-top: 1em;

  dt {
    text-decoration: underline;
  }

  dt::after {
    content: ':';
  }

  a {
    color: inherit;
    word-break: break-all;
  }
}
</style>
